import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import React from "react";
import { useDispatch } from "react-redux";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { EmptyState } from "@/shared/base/components/EmptyState/EmptyState";
import { usePerksManager } from "./usePerksManager";
import { PerkParentItem } from "./PerkParentCard";
import { GroupedPerkSection } from "./GroupedPerkSection";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { usePerkImageSliderModal } from "./PerkImageSliderModal";
import { useCartNavigationHelper } from "../useCartBackButtonHelper";
import { useAppStore } from "@/global/store";
import {
  cartManagerState,
  selectCartEvents,
} from "@/global/store/data/purchaseSeats/cartManager";

/*
 +------------------------------------------------------------------------------+
 * [ perks provider component / base component ]
 +------------------------------------------------------------------------------+ */
export const PerksBase = () => {
  const teamConfig = useTeamConfig();
  const { sm: smDown } = useCssMediaQuery("down");
  const { perksState, perkParents, onLoadPerksBase, hideAddAllButton } =
    usePerksManager();
  const perkImageSliderModal = usePerkImageSliderModal();
  const { backToFindGames } = useCartNavigationHelper();
  const [isRetrying, setIsRetrying] = React.useState(false);
  const dispatch = useDispatch();
  const cartEvents = useAppStore(selectCartEvents);

  React.useEffect(() => {
    (async () => {
      await onLoadPerksBase();
    })();
  }, []);

  // keep suite-all-tickets perk quantities in sync with overflow selection
  const overflowTotalsKey = JSON.stringify(
    cartEvents.map((cartEvent) => ({
      eventCode: cartEvent.eventCode,
      overflowSeats:
        cartEvent.perks
          ?.filter((perk) => perk.perkType === "overflow")
          .reduce((acc, perk) => acc + Number(perk.seatCount || 0), 0) || 0,
    }))
  );

  React.useEffect(() => {
    if (perksState.isLoadingBaseData) return;
    const overflowTotals: Record<string, number> = {};
    (
      JSON.parse(overflowTotalsKey) as {
        eventCode: string;
        overflowSeats: number;
      }[]
    ).forEach((itm) => {
      overflowTotals[itm.eventCode] = itm.overflowSeats;
    });

    // update displayed perk cards
    let hasChanges = false;
    const nextParents = perksState.perkParents.map((parent) => ({
      ...parent,
      perks: parent.perks.map((perk) => {
        if (
          perk.seatCountVariation !== "suite-all-tickets" ||
          !perk.gameEventCode
        )
          return perk;
        const base = perk.baseSeatCount ?? perk.seatCount;
        const nextSeatCount = base + (overflowTotals[perk.gameEventCode] || 0);
        if (nextSeatCount === perk.seatCount) return perk;
        hasChanges = true;
        return { ...perk, seatCount: nextSeatCount };
      }),
    }));
    if (hasChanges) perksState.set({ perkParents: nextParents });

    // update perks already added to the cart
    cartEvents.forEach((cartEvent) => {
      cartEvent.perks?.forEach((perk) => {
        if (perk.seatCountVariation !== "suite-all-tickets") return;
        const base = perk.baseSeatCount ?? perk.seatCount;
        const nextSeatCount =
          base + (overflowTotals[cartEvent.eventCode] || 0);
        if (nextSeatCount !== perk.seatCount)
          dispatch(
            cartManagerState.actions.updatePerkItem({
              id: perk.id,
              gameEventCode: perk.gameEventCode,
              seatCount: nextSeatCount,
            })
          );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overflowTotalsKey, perksState.isLoadingBaseData]);

  const handleRetry = async () => {
    setIsRetrying(true);
    await onLoadPerksBase();
    setIsRetrying(false);
  };

  return (
    <Box>
      {perksState.isLoadingBaseData ? (
        <Grid container spacing={3}>
          {Array.from({ length: 3 }).map((_, idx) => {
            return (
              <Grid item key={idx} xs={12}>
                <DynamicSkeleton
                  rowCount={3}
                  spacing={2}
                  heights={["16px", "16px", "16px"]}
                  widths={["100%", "70%", "40%"]}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : null}

      {!perksState.isLoadingBaseData && perksState.hasLoadError ? (
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"340px"}
          spacing={2}
          p={smDown ? 2 : 4}
        >
          <Typography fontWeight={600} fontSize={"18px"} textAlign={"center"}>
            Unable to load add-ons
          </Typography>
          <Typography
            fontSize={"14px"}
            color="text.secondary"
            textAlign={"center"}
          >
            A network error occurred while loading add-ons. Please try again or
            go back and reselect your seats.
          </Typography>
          <Stack direction={"row"} spacing={2} pt={1}>
            <Button
              variant="outlined"
              onClick={() => backToFindGames()}
              disabled={isRetrying}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              onClick={handleRetry}
              disabled={isRetrying}
            >
              {isRetrying ? "Retrying..." : "Try Again"}
            </Button>
          </Stack>
        </Stack>
      ) : null}

      {!perksState.isLoadingBaseData &&
      !perksState.hasLoadError &&
      perkParents.length == 0 ? (
        <Stack
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"340px"}
          p={smDown ? 2 : 4}
        >
          <EmptyState
            title="No perks available"
            description="There are perks available for purchase at this time."
          />
        </Stack>
      ) : null}

      {!perksState.isLoadingBaseData &&
      !perksState.hasLoadError &&
      perkParents.length > 0 ? (
        <Box>
          <Grid container spacing={2}>
            {(() => {
              const groupedSections =
                teamConfig?.uiConfigs?.perks?.groupedSections || [];
              const sectionByPerkCode = new Map<
                string,
                (typeof groupedSections)[number]
              >();
              groupedSections.forEach((section) =>
                section.perkCodes.forEach((code) =>
                  sectionByPerkCode.set(code, section)
                )
              );

              const visibleParents = perkParents.filter(
                (perkParent) =>
                  !perkParent.perks.every((itm) => itm.isMandatory)
              ); // Exclude fully mandatory groups
              const consumedCodes = new Set<string>();
              const renderEntries: (
                | { type: "single"; parent: (typeof visibleParents)[number] }
                | {
                    type: "group";
                    section: (typeof groupedSections)[number];
                    parents: typeof visibleParents;
                  }
              )[] = [];
              visibleParents.forEach((parent) => {
                if (consumedCodes.has(parent.perkCode)) return;
                const section = sectionByPerkCode.get(parent.perkCode);
                if (!section) {
                  renderEntries.push({ type: "single", parent });
                  return;
                }
                const members = visibleParents.filter((p) =>
                  section.perkCodes.includes(p.perkCode)
                );
                members.forEach((m) => consumedCodes.add(m.perkCode));
                renderEntries.push({ type: "group", section, parents: members });
              });

              return renderEntries.map((entry, idx) => {
                if (entry.type === "group") {
                  return (
                    <Grid key={`group-${idx}`} item xs={12}>
                      <GroupedPerkSection
                        title={entry.section.title}
                        description={entry.section.description}
                        singleSelect={entry.section.singleSelect}
                        perkParents={entry.parents}
                        isOpenByDefault={
                          idx === 0 ||
                          teamConfig?.uiConfigs?.perks?.isOpenByDefault
                        }
                        onClickImageSliderButton={(perkParent) =>
                          perkImageSliderModal.set({
                            isOpen: true,
                            perkCode: perkParent.perkCode,
                            title:
                              teamConfig?.uiConfigs?.perks
                                ?.perkImageSliderModalTitle ||
                              perkParent.title,
                          })
                        }
                      />
                    </Grid>
                  );
                }
                const perkParent = entry.parent;
                return (
                  <Grid key={idx} item xs={12}>
                    <PerkParentItem
                      isOpenByDefault={
                        idx === 0 ||
                        teamConfig?.uiConfigs?.perks?.isOpenByDefault
                      }
                      perkParent={perkParent}
                      hideAddAllButton={hideAddAllButton || false}
                      sliderImages={teamConfig?.uiConfigs?.perks?.images?.[perkParent.perkCode] || [] } // prettier-ignore
                      onClickImageSliderButton={() =>
                        perkImageSliderModal.set({
                          isOpen: true,
                          perkCode: perkParent.perkCode,
                          title:
                            teamConfig?.uiConfigs?.perks
                              ?.perkImageSliderModalTitle || perkParent.title,
                        })
                      }
                      hidePerkPrice={
                        teamConfig?.uiConfigs?.perks?.hidePerkPrice || false
                      }
                    />
                  </Grid>
                );
              });
            })()}
          </Grid>
        </Box>
      ) : null}
    </Box>
  );
};
